'use client';

import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useRef, useState } from 'react';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import type { homeContent } from '@/features/cms/content/home';
import { cn } from '@/lib/utils';
import { QuoteCard } from './quote-card';
type Quote = (typeof homeContent.quotes)[number];
export function QuoteCarousel({
  quotes
}: {
  quotes: Quote[];
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const autoplayPlugin = useRef(Autoplay({
    delay: 5000,
    stopOnMouseEnter: true,
    stopOnInteraction: false
  }));
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const updateFocused = () => {
      const engine = api.internalEngine();
      const currentProgress = api.scrollProgress();
      const snapList = engine.scrollSnapList;
      let minDiff = Infinity;
      let nearestSnapIndex = -1;
      snapList.forEach((snap, snapIndex) => {
        let diff = Math.abs(snap - currentProgress);
        if (engine.options.loop) {
          const diffMinus = Math.abs(snap - currentProgress - 1);
          const diffPlus = Math.abs(snap - currentProgress + 1);
          diff = Math.min(diff, diffMinus, diffPlus);
        }
        if (diff < minDiff) {
          minDiff = diff;
          nearestSnapIndex = snapIndex;
        }
      });
      setFocusedIndex(nearestSnapIndex);
    };
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };
    const handlePointerDown = () => {
      setIsDragging(true);
    };
    const handlePointerUp = () => {
      setIsDragging(false);
    };
    updateFocused();
    api.on('scroll', updateFocused);
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('pointerDown', handlePointerDown);
    api.on('pointerUp', handlePointerUp);
    api.on('settle', handlePointerUp);
    return () => {
      api.off('scroll', updateFocused);
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('pointerDown', handlePointerDown);
      api.off('pointerUp', handlePointerUp);
      api.off('settle', handlePointerUp);
    };
  }, [api]);
  return <div className='mx-auto'>
      <div className='relative'>
        <Carousel grabCursor setApi={setApi} plugins={[autoplayPlugin.current]} opts={{
        align: 'center',
        loop: true,
        skipSnaps: true,
        duration: 20
      }} className='w-full'>
          <div className='py-4'>
            <CarouselContent>
              {quotes?.map((quote, index) => {
              const isActive = current === index;
              const isFocused = focusedIndex === index;
              const isHighlighted = isDragging ? isFocused : isActive;
              return <CarouselItem key={`${quote.person}-${quote.company}-${quote.text.slice(0, 20)}`} className={cn('basis-[260px] cursor-pointer', 'md:basis-[300px]', 'pl-0 md:pl-2')} onClick={() => {
                if (api && !isActive) {
                  api.scrollTo(index);
                }
              }}>
                    <div className={cn('transform-gpu transition-all duration-200 ease-out', isHighlighted ? 'scale-100 opacity-100' : 'scale-90 opacity-60 hover:opacity-70')}>
                      <QuoteCard quote={quote} />
                    </div>
                  </CarouselItem>;
            })}
            </CarouselContent>
          </div>

          <div className='flex items-center justify-center gap-3 pt-8'>
            <CarouselPrevious className='static size-4' variant={'link'} />
            <CarouselNext className='static size-4' variant={'link'} />
          </div>
        </Carousel>
      </div>
    </div>;
}